/**
 * Loop preset bundle support for product cards (collection, homepage, quick add).
 * Mirrors the PDP custom-subscription block flow, but defaults to one-time
 * purchase unless the intercepted form carries a selling_plan input.
 *
 * store.json is fetched ONCE per page load no matter how many cards are on
 * the page; per-card checks are an in-memory id lookup. Bundle config is only
 * fetched when a bundle product is actually added.
 */
(function () {
  const CDN = 'https://cdn.loopwork.co';
  const CART_MODE = 'children_only'; // keep in sync with PDP block default (loop_bundle_cart_mode)

  function getShopDomain() {
    return window.Shopify?.shop || document.documentElement.dataset.shopifyDomain || '';
  }

  /** @type {any} */
  let storeConfig = null;

  // In-memory cache only: fresh fetch on every page load so Loop config
  // changes are picked up immediately, but never more than one request per
  // URL per page no matter how many product cards are rendered.
  const inflight = new Map();

  function fetchJsonCached(cacheKey, url) {
    if (!inflight.has(cacheKey)) {
      inflight.set(
        cacheKey,
        fetch(url)
          .then((res) => (res.ok ? res.json() : null))
          .catch(() => null),
      );
    }
    return inflight.get(cacheKey);
  }

  const ready = (async () => {
    const domain = getShopDomain();
    if (!domain) return;
    try {
      storeConfig = await fetchJsonCached(`loop_store_${domain}`, `${CDN}/${domain}/store.json`);
    } catch (err) {
      console.error('[LoopBundleQuickAdd] Store config error:', err);
    }
  })();

  function isPresetBundle(productId) {
    if (!productId || !storeConfig?.presetBundleShopifyProductIds?.length) return false;
    return storeConfig.presetBundleShopifyProductIds.includes(Number(productId));
  }

  function fetchBundleConfig(domain, productId) {
    return fetchJsonCached(`loop_bundle_${domain}_${productId}`, `${CDN}/${domain}/presetBundles/${productId}.json`);
  }

  async function createBundleTransaction(payload) {
    const authToken = storeConfig?.sentinalAuthToken;
    if (!authToken) throw new Error('Missing Loop auth token');

    const res = await fetch('https://sentinel.loopwork.co/bundles/createPresetBundleTransaction', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: authToken,
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      console.error('[LoopBundleQuickAdd] Transaction error:', errData);
      throw new Error('Loop bundle transaction failed');
    }

    const data = await res.json();
    return data?.data?.transactionId;
  }

  async function getExistingBundleDiscountAttributes() {
    try {
      const res = await fetch('/cart.json');
      const cart = await res.json();
      const attrs = cart.attributes?._loopBundleDiscountAttributes;
      if (!attrs) return {};

      const parsed = JSON.parse(attrs);
      const bundleIdsInCart = new Set(
        cart.items.map((item) => item.properties?._bundleId || item.properties?._loopBundleTxnId).filter(Boolean),
      );

      return Object.keys(parsed)
        .filter((key) => bundleIdsInCart.has(key))
        .reduce((obj, key) => {
          obj[key] = parsed[key];
          return obj;
        }, {});
    } catch {
      return {};
    }
  }

  /**
   * Add a Loop preset bundle to the cart.
   * @param {{ productId: string|number, variantId: string|number, quantity?: number, sellingPlanId?: string|number, sectionIds?: string[] }} opts
   * @returns {Promise<object>} cart/add.js response
   */
  async function addToCart({ productId, variantId, quantity = 1, sellingPlanId = '', sectionIds = [] }) {
    await ready;
    const domain = getShopDomain();

    const bundleConfig = await fetchBundleConfig(domain, productId);
    if (!bundleConfig) throw new Error('Could not load Loop bundle config');

    const bundleVariant = bundleConfig.variants?.find((v) => String(v.shopifyId) === String(variantId));
    if (!bundleVariant) throw new Error('Variant not found in Loop bundle config');

    const isSubscription = !!sellingPlanId;
    const discount = bundleVariant.mappedDiscounts?.find((d) =>
      isSubscription ? d.purchaseType === 'SUBSCRIPTION' : d.purchaseType !== 'SUBSCRIPTION',
    );
    if (!discount) {
      console.error('[LoopBundleQuickAdd] No discount found! Available:', bundleVariant.mappedDiscounts);
      throw new Error('No Loop discount for this purchase type');
    }

    const transactionPayload = {
      presetProductShopifyId: Number(bundleConfig.id),
      presetDiscountId: discount.id,
      presetVariantShopifyId: Number(variantId),
      totalQuantity: quantity,
    };
    if (sellingPlanId) transactionPayload.sellingPlanShopifyId = Number(sellingPlanId);

    const bundleTransactionId = await createBundleTransaction(transactionPayload);
    if (!bundleTransactionId) throw new Error('Failed to create bundle transaction');

    const addParentForced = CART_MODE === 'parent_only' || CART_MODE === 'both';
    const addChildProducts = CART_MODE !== 'parent_only';

    const discountMultiplier = addParentForced ? 1 : quantity;
    const discountValue =
      sellingPlanId && discount.sellingPlanComputedDiscounts?.[sellingPlanId]
        ? discount.sellingPlanComputedDiscounts[sellingPlanId] * discountMultiplier
        : (discount.oneTimeDiscount || 0) * discountMultiplier;

    const existingAttrs = await getExistingBundleDiscountAttributes();

    const formData = {
      items: [],
      attributes: {
        _loopBundleDiscountAttributes: JSON.stringify({
          ...existingAttrs,
          [bundleTransactionId]: {
            discountType: discount.type,
            discountValue: discount.value,
            discountComputedValue: discountValue,
          },
        }),
      },
    };

    const bundleName = bundleConfig.name;
    const loopMeta = {
      _loopPresetProductId: String(bundleConfig.id),
      _loopPresetVariantId: String(bundleVariant.shopifyId),
      _loopPackQty: String(quantity),
      _loopAddParentForced: String(addParentForced),
    };

    if (addParentForced) {
      const parentItem = {
        id: bundleVariant.shopifyId,
        quantity: quantity,
        properties: {
          _loopBundleTxnId: bundleTransactionId,
          _isPresetBundleProduct: true,
          _bundleName: bundleName,
          bundleName: bundleName,
          ...loopMeta,
        },
      };
      if (sellingPlanId) parentItem.selling_plan = Number(sellingPlanId);
      formData.items.push(parentItem);
    }

    if (addChildProducts) {
      (bundleVariant.mappedProductVariants || []).forEach((child) => {
        const item = {
          id: child.shopifyId,
          quantity: child.quantity * quantity,
          properties: {
            _bundleId: bundleTransactionId,
            _loopBundleTxnId: bundleTransactionId,
            _isPresetBundleProduct: true,
            _bundleName: bundleName,
            bundleName: bundleName,
            ...loopMeta,
          },
        };
        if (sellingPlanId) item.selling_plan = Number(sellingPlanId);
        formData.items.push(item);
      });
    }

    if (!formData.items.length) throw new Error('Loop bundle cart payload is empty');

    if (sectionIds.length > 0) {
      formData.sections = sectionIds.join(',');
      formData.sections_url = window.location.pathname;
    }

    const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
    const cartResponse = await fetch(cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    });

    const cartData = await cartResponse.json().catch(() => null);
    if (!cartData) throw new Error('Invalid response from server. Please try again.');
    if (cartData.status) throw new Error(cartData.description || cartData.message || 'Could not add to cart');

    return cartData;
  }

  // ========== BUTTON SWAP (PDP-style) ==========
  // add-to-cart-button.liquid renders a hidden custom button next to the
  // native add-to-cart-component. When the product is a Loop preset bundle we
  // hide the native button and show the custom one, whose click goes through
  // the Loop CDN flow — same idea as the PDP custom-subscription widget.

  async function handleLoopClick(button, productId) {
    if (button.disabled) return;

    const form = button.closest('form');
    const variantId = form?.querySelector('input[name="id"]')?.value;
    if (!variantId) {
      console.error('[LoopBundleQuickAdd] No variant input found for product', productId);
      return;
    }
    const quantity =
      parseInt(form?.querySelector('input[name="quantity"], select[name="quantity"]')?.value || '1', 10) || 1;
    // Cards have no plan picker, so this is empty -> one-time purchase by default.
    const sellingPlanId = form?.querySelector('[name="selling_plan"]')?.value || '';

    button.disabled = true;
    button.classList.add('is-loading');

    try {
      const sectionIds = [];
      document.querySelectorAll('cart-items-component').forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionIds.push(item.dataset.sectionId);
        }
      });

      const cartData = await addToCart({ productId, variantId, quantity, sellingPlanId, sectionIds });

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: {},
            sourceId: 'loop-bundle-quick-add',
            data: {
              source: 'loop-bundle-quick-add',
              sections: cartData.sections || {},
              itemCount: quantity,
            },
          },
        }),
      );

      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer?.open) cartDrawer.open();

      // Play the added checkmark animation (same CSS as the native button).
      button.dataset.added = 'true';
      setTimeout(() => button.removeAttribute('data-added'), 1500);
    } catch (err) {
      console.error('[LoopBundleQuickAdd] Add to cart error:', err);
      alert('Error adding bundle to cart. Please try again.');
    } finally {
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  }

  function swapLoopButtons() {
    document.querySelectorAll('button[data-loop-custom-atc][hidden]').forEach((button) => {
      if (!isPresetBundle(button.dataset.productId)) return;

      // Skip buttons in a section that has the PDP custom-subscription widget —
      // that widget owns the Loop flow there (with one-time vs subscription).
      const section = button.closest('.shopify-section, section');
      if (section?.querySelector('[data-loop-bundle-cart-mode]')) return;

      const nativeAtc =
        button.previousElementSibling?.tagName === 'ADD-TO-CART-COMPONENT'
          ? button.previousElementSibling
          : button.parentElement?.querySelector('add-to-cart-component');

      if (nativeAtc) nativeAtc.style.display = 'none';
      button.hidden = false;
    });
  }

  // One delegated listener covers buttons added later (quick-add modal,
  // re-rendered sections) without per-button binding.
  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element && event.target.closest('button[data-loop-custom-atc]');
    if (!button) return;
    event.preventDefault();
    handleLoopClick(button, button.dataset.productId);
  });

  function revealButtons() {
    document.documentElement.removeAttribute('data-loop-atc-pending');
  }

  async function activate() {
    // Safety net: never leave buttons hidden if the Loop CDN hangs or errors.
    const revealFallback = setTimeout(revealButtons, 4000);

    try {
      await ready;
      if (storeConfig?.presetBundleShopifyProductIds?.length) {
        swapLoopButtons();
        // Quick-add modal content and re-rendered sections arrive later.
        new MutationObserver(swapLoopButtons).observe(document.body, { childList: true, subtree: true });
      }
    } finally {
      clearTimeout(revealFallback);
      revealButtons();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', activate);
  } else {
    activate();
  }

  window.LoopBundleQuickAdd = {
    ready,
    isPresetBundle,
    addToCart,
  };
})();
